/**
 * PatientBanner — displays patient demographics and encounter info.
 * Matches the style strip used in Cerner mPages and eCW Healow.
 */
import { useSession } from '../context/SessionContext';
import type { Patient, Encounter, EHRTheme } from '../types';
import clsx from 'clsx';

interface Props {
  patient: Patient;
  encounter: Encounter;
}

function formatDOB(dob: string): string {
  const d = new Date(dob + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

function themeHeaderBg(theme: EHRTheme): string {
  if (theme === 'cerner') return 'bg-cerner-blue text-white';
  if (theme === 'ecw') return 'bg-ecw-teal text-white';
  return 'bg-gray-700 text-white';
}

export function PatientBanner({ patient, encounter }: Props) {
  const { launch } = useSession();

  return (
    <div className={clsx('px-4 py-2.5 text-sm', themeHeaderBg(launch.theme))}>
      {/* Top row: name + MRN */}
      <div className="flex items-center justify-between flex-wrap gap-x-4 gap-y-1">
        <div className="flex items-center gap-3 min-w-0">
          {/* Patient avatar initials */}
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold uppercase tracking-wide">
            {patient.firstName[0]}{patient.lastName[0]}
          </div>
          <div className="min-w-0">
            <span className="font-semibold text-base leading-tight">
              {patient.lastName}, {patient.firstName}
            </span>
            <span className="ml-2 text-white/70 text-xs">MRN: {patient.mrn}</span>
          </div>
        </div>

        {/* Encounter info */}
        <div className="text-white/80 text-xs text-right">
          <span className="font-medium text-white">{encounter.encounterType}</span>
          <span className="mx-1.5">·</span>
          <span>{new Date(encounter.encounterDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
        </div>
      </div>

      {/* Bottom row: demographics chips */}
      <div className="mt-1.5 flex items-center gap-3 text-white/80 text-xs flex-wrap">
        <DemoBadge label="DOB" value={formatDOB(patient.dateOfBirth)} />
        <span className="text-white/40">|</span>
        <DemoBadge label="Age" value={`${patient.age}`} />
        <span className="text-white/40">|</span>
        <DemoBadge label="Sex" value={patient.gender} />
        <span className="text-white/40">|</span>
        <DemoBadge label="Plan" value={patient.insurancePlan} />
        {patient.riskAdjustmentEligible && (
          <>
            <span className="text-white/40">|</span>
            <span className="bg-emerald-500/30 text-emerald-100 px-1.5 py-0.5 rounded text-2xs font-semibold uppercase tracking-wide">
              MA Eligible
            </span>
          </>
        )}
        <span className="text-white/40">|</span>
        <DemoBadge label="PCP" value={patient.pcp} />
        <span className="text-white/40">|</span>
        <DemoBadge label="Attending" value={encounter.attendingPhysician} />
      </div>
    </div>
  );
}

function DemoBadge({ label, value }: { label: string; value: string }) {
  return (
    <span>
      <span className="text-white/50 mr-1">{label}:</span>
      <span className="font-medium">{value}</span>
    </span>
  );
}
